import { faBriefcase } from "@fortawesome/pro-solid-svg-icons/faBriefcase";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faCircleHeart } from "@fortawesome/pro-solid-svg-icons/faCircleHeart";
import { faGamepad } from "@fortawesome/pro-solid-svg-icons/faGamepad";
import { faShieldCheck } from "@fortawesome/pro-solid-svg-icons/faShieldCheck";
import { faShieldQuartered } from "@fortawesome/pro-solid-svg-icons/faShieldQuartered";
import { faThumbsUp } from "@fortawesome/pro-solid-svg-icons/faThumbsUp";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import { Avatar, Box, CardContent, Dialog, DialogContent, Grid, Pagination, Typography } from '@mui/material';
import { APP_PAGE_SIZE_DEFAULT } from 'app/appConstants';
import { CorePermissions } from "app/appPermissions";
import { MprcCard, MprcCardActions, MprcConfirmDialog, MprcIconButtonMoreOptionsDropdown, MprcIconText, MprcText } from 'components';
import { MprcFilterBoxContent, MprcFilterCount } from 'components/filter';
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { IconAdd } from 'components/icons';
import { IconSort } from 'components/icons/IconSort';
import { MprcBarTitle } from 'components/MprcBarTitle';
import { MprcContainer } from 'components/MprcContainer';
import { MprcDialogTitleClose } from 'components/MprcDialogTitleClose';
import { MprcDropdownBuildGroupedItems, MprcDropDownItemAny, MprcDropdownItems, MprcIconButtonDropdown } from 'components/MprcIconButtonDropdown';
import { MprcRequestErrorDialog } from 'components/MprcRequestErrorDialog';
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcRequestIndicator } from 'components/MprcRequestIndicator';
import { MprcSwitch } from "components/MprcSwitch";
import { CoreFilterCommand, GroupItemDtoPaginatedList, ManagedUserTopicsOrderBy, ManagedUserTopicsQueryResponse, PaginatedUserTopics, UserItemDtoPaginatedList } from 'generated/CoreApi_v1';
import moment from "moment";
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ListGroupsDrawer } from "views/foundation/group/drawers/ListGroupsDrawer";
import { TopicAutoCompleteHierarchical } from "views/foundation/topic/components/TopicAutoCompleteHierarchical";
import { AdvancedFilter, IAdvFilterCommand, IAdvFilterParams } from "views/general/AdvancedFilter";
import { ListUsersDrawer } from "../../drawers/ListUsersDrawer";
import { EditUserHeader } from './EditUserHeader';

type FormMode = "adding" | "filtering";

type TopicMenuOptions = "viewProfile" | "viewUserEvolution" | "addEndorsement" | "removeEndorsement" | "grantSpecialistSeal" | "revokeSpecialistSeal" | "changeLink" | "linkedBy" | "remove";

interface TopicUserFilterOptions extends IAdvFilterParams<ManagedUserTopicsOrderBy> {
}

interface IProps {
    userId: string;
    userName: string;
    editMode: "default" | "profile";
    viewMode?: "dialog" | "form";
    onClose: () => void;
}

/**
 * Edição de Usuário - Interesse do usuário.
 * @param props
 */
export const EditUserTopics: React.FC<IProps> = (props) => {

    const { userId, userName, viewMode, onClose, editMode } = props;

    const api = useCoreApi();
    const { permissionExplorer, user } = useSession();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [formMode, setFormMode] = useState<FormMode>("adding");
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [requiredLink, setRequiredLink] = useState<boolean>(false);
    const [viewData, setViewData] = useState<PaginatedUserTopics | null>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [dialog, setDialog] = useState<any>();
    const [deleting, setDeleting] = useState<string | null>(null);

    const pageNumber = useRef<number>(1);
    const filterOptions = useRef<TopicUserFilterOptions>({
        searchText: "",
        advFilters: [],
        order: ManagedUserTopicsOrderBy.Name,
    });

    const filtered = filterOptions.current.advFilters.length > 0 || (filterOptions.current.searchText.length || 0) > 0;

    ///
    const loadPage = (page: number) => {
        pageNumber.current = page;
        setLoading(true);
    };

    ///
    const handleFilterChange = (filter: TopicUserFilterOptions) => {
        filterOptions.current = filter;
        loadPage(1);
    };

    ///
    const handleResetFilter = () => {
        filterOptions.current = { ...filterOptions.current, searchText: "", advFilters: [] };
        loadPage(1);
    };

    const getCommands = (): IAdvFilterCommand[] => {
        const cmds: IAdvFilterCommand[] = [
            { command: CoreFilterCommand.TopicUserRequiredLink, displayName: t("Topic.Edit.Users.Cmd.RequiredLink"), group: 1 },
            { command: CoreFilterCommand.TopicUserOptionalLink, displayName: t("Topic.Edit.Users.Cmd.OptionalLink"), group: 1 },

            { command: CoreFilterCommand.TopicUserWithEndorsement, displayName: t("Topic.Edit.Users.Cmd.WithEndorsement"), group: 2 },
            { command: CoreFilterCommand.TopicUserWithProvenKnowledge, displayName: t("Topic.Edit.Users.Cmd.WithProvenKnowledge"), group: 2 },

            { command: CoreFilterCommand.TopicUserIsVerifiedKnower, displayName: t("Topic.Edit.Users.Cmd.IsVerifiedKnower"), group: 3 },
            { command: CoreFilterCommand.TopicUserIsSpecialist, displayName: t("Topic.Edit.Users.Cmd.IsSpecialist"), group: 3 },
        ];
        return cmds;
    };

    ///
    const getMenuItems = (item: ManagedUserTopicsQueryResponse): MprcDropDownItemAny[] => {

        const group2: MprcDropdownItems<TopicMenuOptions> = [];

        if (userId != user.userId && !item.readonly) {
            group2.push({
                text:
                    <>
                        {item.loggedUserHasEndorsed ? <FontAwesomeIcon fixedWidth icon={faCheck} /> : null}
                        <Box ml={0.5}>{t("Topic.Edit.Users.AddEndorsementAction")}</Box>
                    </>,
                value: item.loggedUserHasEndorsed ? "removeEndorsement" : "addEndorsement"
            });
        }

        if (item.canGrantSpecialistSeal) {
            group2.push({
                text:
                    <>
                        {item.isSpecialist ? <FontAwesomeIcon fixedWidth icon={faCheck} /> : null}
                        <Box ml={0.5}>{t("Topic.Edit.Users.GrantSpecialistSealAction")}</Box>
                    </>,
                value: item.isSpecialist ? "revokeSpecialistSeal" : "grantSpecialistSeal"
            });
        }

        const group3: MprcDropdownItems<TopicMenuOptions> = [];
        if (permissionExplorer.hasOne([CorePermissions.TopicsLinkUsers])) {
            if (item.dynamicLink === true) {
                group3.push({ text: <MprcText color="warning">{t("Topic.Edit.Users.DynamicAssociationAction")}</MprcText>, value: "linkedBy" });
            }
            else if (item.requiredLink === false || viewData?.canAddRequiredTopics === true) {
                group3.push({
                    text: t("Topic.Edit.Users.ChangeToText") + " " + (item.requiredLink ? t("Topic.Edit.Users.OptionalText") : t("Topic.Edit.Users.RequiredText")),
                    value: "changeLink"
                });
            }
        }
        if (item.canDelete) {
            group3.push({ text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "remove" });
        }

        return MprcDropdownBuildGroupedItems<TopicMenuOptions>([group2, group3]);
    };

    ///
    const handleAdd = (topicId: string) => {
        setSaving(true);
        // Se o usuário do perfil for o usuário logado, adiciona interesse e subscreve no tópico.
        if (editMode == "profile" && !requiredLink && user.userId == userId) {
            api.v1.knowledgeSubcription.subscribeTopic({ topicId })
                .then(result => {
                    if (result.data === true) {
                        enqueueSnackbar(t("User.Edit.Topics.AddSuccessMessage"), { variant: "success" });
                        setLoading(true);
                    }
                    else {
                        enqueueSnackbar(t("User.Edit.Topics.AddFailMessage"), { variant: "error" });
                    }
                })
                .catch(e => MprcRequestSnackbarErrorReport(e))
                .finally(() => setSaving(false));
        } else {
            api.v1.topicManagement.addUserToTopic({
                requiredLink,
                topicId: topicId,
                userId
            }).then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("User.Edit.Topics.AddSuccessMessage"), { variant: "success" });
                    setLoading(true);
                }
                else {
                    enqueueSnackbar(t("User.Edit.Topics.AddFailMessage"), { variant: "error" });
                }
            })
                .catch(e => MprcRequestSnackbarErrorReport(e))
                .finally(() => setSaving(false));
        }
    };

    ///
    const handleChangeLink = (item: ManagedUserTopicsQueryResponse) => {
        setSaving(true);
        api.v1.topicManagement.changeUserLink({
            topicId: item.topicId,
            userId: userId,
            isRequiredLink: item.requiredLink
        })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Topic.Edit.Users.LinkChangedSuccessMessage"), { variant: "success" });
                    setLoading(true);
                } else {
                    enqueueSnackbar(t("Topic.Edit.Users.LinkChangedFailMessage"), { variant: "error" });
                }
            })
            .catch(e => setViewError(e))
            .finally(() => setSaving(false));
    };

    ///
    const handleGrantSpecialistSeal = (item: ManagedUserTopicsQueryResponse) => {
        setDialog(<MprcConfirmDialog
            open
            severity="danger"
            message={t("Topic.Edit.Users.ConfirmGrantSpecialistSealMessage")}
            onClose={(result) => {
                if (result) {
                    setSaving(true);
                    api.v1.userManagement.grantSpecialistSeal({ topicId: item.topicId, userId: userId })
                        .then(result => {
                            if (result.data === true) {
                                enqueueSnackbar(t("Topic.Edit.Users.GrantSpecialistSealSuccessMessage"), { variant: "success" });
                                setLoading(true);
                            } else {
                                enqueueSnackbar(t("Topic.Edit.Users.GrantSpecialistSealFailMessage"), { variant: "error" });
                            }
                        }).catch(e => setViewError(e))
                        .finally(() => setSaving(false));
                }
                setDialog(null);
            }}
        />);
    };

    ///
    const handleRevokeSpecialistSeal = (item: ManagedUserTopicsQueryResponse) => {
        setDialog(<MprcConfirmDialog
            open
            severity="danger"
            message={t("Topic.Edit.Users.ConfirmRevokeSpecialistSealMessage")}
            onClose={(result) => {
                if (result) {
                    setSaving(true);
                    api.v1.userManagement.revokeSpecialistSeal({ topicId: item.topicId, userId: userId })
                        .then(result => {
                            if (result.data === true) {
                                enqueueSnackbar(t("Topic.Edit.Users.RevokeSpecialistSealSuccessMessage"), { variant: "success" });
                                setLoading(true);
                            } else {
                                enqueueSnackbar(t("Topic.Edit.Users.RevokeSpecialistSealFailMessage"), { variant: "error" });
                            }
                        }).catch(e => setViewError(e))
                        .finally(() => setSaving(false));
                }
                setDialog(null);
            }}
        />);
    };

    ///
    const handleAddEndorsement = (item: ManagedUserTopicsQueryResponse) => {
        setSaving(true);
        api.v1.userManagement.addTopicEndorment({ topicId: item.topicId!, userId: userId })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Topic.Edit.Users.AddEndorsementSuccessMessage"), { variant: "success" });
                    setLoading(true);
                } else {
                    enqueueSnackbar(t("Topic.Edit.Users.AddEndorsementFailMessage"), { variant: "error" });
                }
            }).catch(e => setViewError(e))
            .finally(() => setSaving(false));
    };

    ///
    const handleRemoveEndorsement = (item: ManagedUserTopicsQueryResponse) => {
        setSaving(true);
        api.v1.userManagement.deleteTopicEndorment({ topicId: item.topicId!, userId: userId })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Topic.Edit.Users.RemoveEndorsementSuccessMessage"), { variant: "success" });
                    if (viewData?.items.length == 1 && pageNumber.current > 1) pageNumber.current--;
                    setLoading(true);
                } else {
                    enqueueSnackbar(t("Topic.Edit.Users.RemoveEndorsementFailMessage"), { variant: "error" });
                }
            }).catch(e => setViewError(e))
            .finally(() => setSaving(false));
    };

    ///
    const handleOption = (item: ManagedUserTopicsQueryResponse, option: TopicMenuOptions) => {
        switch (option) {
            case "changeLink":
                handleChangeLink(item);
                break;

            case "addEndorsement":
                handleAddEndorsement(item);
                break;

            case "removeEndorsement":
                handleRemoveEndorsement(item);
                break;

            case "grantSpecialistSeal":
                handleGrantSpecialistSeal(item);
                break;

            case "revokeSpecialistSeal":
                handleRevokeSpecialistSeal(item);
                break;

            case "linkedBy":
                setDialog(
                    <ListGroupsDrawer
                        onClose={() => setDialog(null)}
                        onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                            new Promise<GroupItemDtoPaginatedList>((resolve, reject) => {
                                api.v1.topicManagement.getAssociatedGroups({ topicId: item.topicId!, userId: userId, searchText, pageNumber, pageSize }, { signal })
                                    .then(result => resolve(result.data))
                                    .catch(e => reject(e));
                            })
                        }
                    />
                );
                break;

            case "remove":
                setDialog(
                    <MprcConfirmDialog
                        open
                        severity="danger"
                        message={<span>{t("User.Edit.Topics.ConfirmDeleteMessage")}</span>}
                        onClose={
                            (result) => {
                                if (result) {
                                    setDeleting(item.topicId);
                                }
                                setDialog(null);
                            }
                        }
                    />
                );
                break;
        }
    };

    ///
    const showEndorsers = (item: ManagedUserTopicsQueryResponse) => {
        setDialog(
            <ListUsersDrawer
                onClose={() => setDialog(null)}
                onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                    new Promise<UserItemDtoPaginatedList>((resolve, reject) => {
                        api.v1.topicManagement.getEndorserUsers({ topicId: item.topicId!, userId: userId, searchText, pageNumber, pageSize }, { signal })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
            />
        );
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////////

    // Ao remover...
    useEffect(() => {
        async function postData() {

            if (deleting) {
                try {
                    if ((await api.v1.topicManagement.removeUserFromTopic({ topicId: deleting!, userId })).data === true) {
                        enqueueSnackbar(t("User.Edit.Topics.DeleteSuccessMessage"), { variant: "success" });
                        if (viewData!.items.length == 1 && pageNumber.current > 1) pageNumber.current--;
                        setLoading(true);
                    }
                    else
                        enqueueSnackbar(t("User.Edit.Topics.DeleteFailMessage"), { variant: "error" });
                }
                catch (e) {
                    setViewError(e);
                }
            }

            setDeleting(null);
        }

        postData();

    }, [deleting]);

    // Ao carregar...
    useEffect(() => {

        async function getData() {
            try {
                var result = await api.v1.userManagement.getTopics({
                    userId,
                    searchText: filterOptions.current.searchText ?? null,
                    pageNumber: pageNumber.current,
                    pageSize: APP_PAGE_SIZE_DEFAULT,
                    hasEndorsement: filterOptions.current.advFilters.findIndex(p => p.command == CoreFilterCommand.TopicUserWithEndorsement) != -1,
                    hasProvenKnowdledge: filterOptions.current.advFilters.findIndex(p => p.command == CoreFilterCommand.TopicUserWithProvenKnowledge) != -1,
                    isSpecialist: filterOptions.current.advFilters.findIndex(p => p.command == CoreFilterCommand.TopicUserIsSpecialist) != -1,
                    hasProvenKnowledgeSeal: filterOptions.current.advFilters.findIndex(p => p.command == CoreFilterCommand.TopicUserIsVerifiedKnower) != -1,
                    viewRequiredLink: filterOptions.current.advFilters.findIndex(p => p.command == CoreFilterCommand.TopicUserRequiredLink) != -1,
                    viewOptionalLink: filterOptions.current.advFilters.findIndex(p => p.command == CoreFilterCommand.TopicUserOptionalLink) != -1,
                    orderBy: filterOptions.current.order
                });
                setViewData(result.data);
            }
            catch (e: any) {
                setViewData(null);
                setViewError(e as Error);
            }
            setLoading(false);
        }

        if (loading) getData();
    }, [loading]);

    useEffect(() => {
        if (formMode == "adding") handleResetFilter();
    }, [formMode]);

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const RenderFilter = (
        <MprcCardFilterBox>
            <MprcFilterBoxContent
                title={t("User.Edit.Topics.Title")}
                filterAction={{
                    readonly: false,
                    filtered: formMode == "filtering",
                    onClick: () => setFormMode(formMode == "filtering" ? "adding" : "filtering")
                }}
                actions={
                    [
                        <MprcIconButtonDropdown
                            key="btnSort"
                            icon={<IconSort />}
                            title={t("shared:Filter.OrderBy")}
                            onChange={(order) => handleFilterChange({ ...filterOptions.current, order })}
                            items={[
                                { text: t("Topic.Edit.Users.NameText"), value: ManagedUserTopicsOrderBy.Name },
                                { text: t("Topic.Edit.Users.LinkTypeText"), value: ManagedUserTopicsOrderBy.RequiredLink },
                                { text: t("Topic.Edit.Users.GreaterKnowledgeText"), value: ManagedUserTopicsOrderBy.GreaterKnowledge },
                                { text: t("Topic.Edit.Users.MoreEndorsementsText"), value: ManagedUserTopicsOrderBy.MoreEndorsements }
                            ]}
                            selected={filterOptions.current.order}
                            menuItemCheck
                            color="muted"
                        />
                    ]
                }
            >
                {
                    formMode == "adding"
                        ?
                        <>
                            {editMode == "profile" &&
                                <MprcContainer margin="dense">
                                    <Typography variant="caption">
                                        <Trans i18nKey={"User.Edit.Topics.Hint"} t={t} />
                                    </Typography>
                                </MprcContainer>
                            }
                            {viewData?.canAddTopics &&
                                <Grid container spacing={1}>
                                    <Grid size="grow">
                                        <TopicAutoCompleteHierarchical
                                            selectedMode="free"
                                            placeholder={t("User.Edit.Topics.AddPlaceholder")}
                                            multiSelect={false}
                                            selectedItems={[]}
                                            isInterestAssigment={editMode == "profile" && userId == user.userId}
                                            onChange={(items) => items.length > 0 && handleAdd(items[0].key)}
                                            onError={e => setViewError(e)}
                                            iconStart={<IconAdd />}
                                        />
                                    </Grid>
                                    {
                                        viewData?.canAddRequiredTopics &&
                                        <Grid>
                                            <MprcSwitch
                                                label={t("User.Edit.Topics.RequiredText")}
                                                checked={requiredLink}
                                                onChange={(e) => setRequiredLink(e.target.checked)}
                                            />
                                        </Grid>
                                    }
                                </Grid>
                            }
                        </>
                        :
                        <AdvancedFilter
                            filter={filterOptions.current}
                            leftDropdowns={[{
                                filterCommands: getCommands(),
                                textOrIcon: <ArrowDropDownCircleIcon />,
                                title: t("shared:AdvFilter.Title")
                            }]}
                            searchTextInFilterLine
                            leftDropdownAlign="end"
                            onFilterChange={(filter) => handleFilterChange({ ...filterOptions.current, ...filter })}
                            onError={error => MprcRequestSnackbarErrorReport(error)}
                        />
                }
            </MprcFilterBoxContent>
        </MprcCardFilterBox>
    );

    ///
    const RenderGrid =
        <>
            <Grid container spacing={2}>
                {
                    viewData?.items.map((item, index) => {
                        var menuItems = getMenuItems(item);
                        return (
                            <Grid key={index} width="100%">
                                <Grid container justifyContent="flex-start" alignItems="center" spacing={1} width="100%" wrap="nowrap">
                                    <Grid>
                                        <Avatar
                                            sx={{ width: 32, height: 32 }}
                                            src={item.thumbnailUrl}
                                        />
                                    </Grid>
                                    <Grid size="grow" overflow="hidden">
                                        <Grid container direction="column" flexWrap="nowrap" width="100%">
                                            <Grid>
                                                <Grid container spacing={.5} alignItems="center" flexWrap="nowrap">
                                                    <Grid overflow="hidden">
                                                        <Typography variant="h5" noWrap>{item.name}</Typography>
                                                    </Grid>
                                                    {
                                                        item.isSpecialist &&
                                                        <Grid>
                                                            <MprcText color="success">
                                                                <FontAwesomeIcon
                                                                    title={t("User.Edit.Topics.IsSpecialistToolTip", { name: item.specialistSealGrantedBy, date: moment(item.specialistSealGrantedOn).format("L") })}
                                                                    icon={faShieldCheck}
                                                                />
                                                            </MprcText>
                                                        </Grid>
                                                    }
                                                    {
                                                        item.hasProvenKnowledgeSeal && !item.isSpecialist &&
                                                        <Grid>
                                                            <MprcText color="info">
                                                                <FontAwesomeIcon
                                                                    title={t("User.Edit.Topics.VerifiedKnowerTooltip")}
                                                                    icon={faShieldQuartered}
                                                                />
                                                            </MprcText>
                                                        </Grid>
                                                    }
                                                    {
                                                        menuItems.length > 0 &&
                                                        <Grid>
                                                            <MprcIconButtonMoreOptionsDropdown
                                                                items={menuItems}
                                                                onChange={(selectedOption) => handleOption(item, selectedOption)}
                                                            />
                                                        </Grid>
                                                    }
                                                </Grid>
                                            </Grid>
                                            <Grid>
                                                <Grid container justifyContent="flex-start" alignItems="center" spacing={4}>
                                                    <Grid>
                                                        <MprcIconText
                                                            iconColor="muted.light"
                                                            typographProps={{ variant: "captionHeader" }}
                                                            icon={item.requiredLink ? faBriefcase : faCircleHeart}
                                                            text={item.requiredLink ? t("User.Edit.Topics.RequiredText") : t("User.Edit.Topics.OptionalText")}
                                                            iconSize="1x"
                                                        />
                                                    </Grid>
                                                    <Grid size="grow">
                                                        <Grid container alignItems="center" spacing={4}>
                                                            {
                                                                item.provenKnowledgePercent > 0 &&
                                                                <Grid>
                                                                    <MprcIconText
                                                                        icon={faGamepad}
                                                                        text={`${item.provenKnowledgePercent}%`}
                                                                        iconSize="1x"
                                                                        iconColor="muted.light"
                                                                        color="muted.light"
                                                                    />
                                                                </Grid>
                                                            }
                                                            {
                                                                item.endorsements > 0 &&
                                                                <Grid mr={2}>
                                                                    <MprcIconText
                                                                        iconColor="muted.light"
                                                                        typographProps={{ variant: "captionHeader" }}
                                                                        onClick={() => showEndorsers(item)}
                                                                        icon={faThumbsUp}
                                                                        text={t("Topic.Edit.Users.EndorsedText", { count: item.endorsements })}
                                                                        iconSize="1x"
                                                                    />
                                                                </Grid>
                                                            }
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                </Grid>
                            </Grid>
                        );
                    })
                }
            </Grid>
        </>;

    ///
    const RenderContent = () => {
        return (
            <>
                <MprcRequestIndicator
                    visible={loading || saving || deleting != undefined}
                    type={loading ? "request" : "post"}
                    restoreFocus={false}
                />

                <MprcRequestErrorDialog
                    error={viewError}
                    onClose={() => { setViewError(undefined); }}
                />

                {dialog}

                <MprcContainer>
                    <MprcCard>
                        {RenderFilter}
                        <CardContent>
                            <MprcContainer>

                                <MprcContainer>
                                    <MprcFilterCount
                                        loading={loading}
                                        isEmpty={(viewData?.totalRows ?? 0) == 0}
                                        countText={t("User.Edit.Topics.RecordsFoundText", { count: viewData?.totalRows ?? 0 })}
                                        filter={{
                                            active: filtered,
                                            text: t("User.Edit.Topics.RecordsFilteredText", { count: viewData?.totalRows ?? 0 }),
                                            onResetFilter: handleResetFilter
                                        }}
                                    />
                                </MprcContainer>
                                {
                                    viewData && viewData.totalRows > 0 && RenderGrid
                                }
                                <MprcCardActions>
                                    {
                                        viewData && viewData.totalPages > 1 &&
                                        <Pagination
                                            shape="rounded"
                                            color="primary"
                                            page={viewData.pageNumber}
                                            count={viewData.totalPages}
                                            onChange={(e, page) => loadPage(page)}
                                        />
                                    }
                                </MprcCardActions>
                            </MprcContainer>
                        </CardContent>
                    </MprcCard>
                </MprcContainer>
            </>
        );
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {
                viewMode == "dialog"
                    ? (
                        <Dialog
                            open
                            fullWidth
                            maxWidth="lg"
                        >
                            <MprcDialogTitleClose
                                title={userName}
                                onClose={onClose}
                            />
                            <DialogContent sx={{ height: "100vh" }}>
                                {RenderContent()}
                            </DialogContent>
                        </Dialog>
                    )
                    : (
                        <Grid container direction="column" flexWrap="nowrap" spacing={2} height="100%">
                            <Grid>
                                <EditUserHeader
                                    onClose={onClose}
                                    recordChanged={false}
                                    reloadOnClose={false}
                                    actions={[
                                        <MprcBarTitle>
                                            {editMode == "profile" && userId == user.userId ? t("User.Common.YourProfileText") : userName}
                                        </MprcBarTitle>
                                    ]}
                                />
                            </Grid>
                            <Grid size="grow" height="100%">
                                {RenderContent()}
                            </Grid>
                        </Grid>
                    )
            }
        </>
    );
};
